import { useEffect, useState, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSessionStore } from "../../../store/session-store";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { useUnstractSubscriptionPlanStore } from "../../store/unstract-subscription-plan-store";
import { useAlertStore } from "../../../store/alert-store";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import { GenericLoader } from "../../../components/generic-loader/GenericLoader";
import useRequestUrl from "../../../hooks/useRequestUrl";

function UnstractSubscriptionCheck() {
  const [isLoading, setIsLoading] = useState(true);
  const { sessionDetails } = useSessionStore();
  const { updateSubscriptionPlan } = useUnstractSubscriptionPlanStore();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { getUrl } = useRequestUrl();
  const location = useLocation();

  const SUBSCRIPTION_DETAILS_PATH = "/subscription/details";
  const SUBSCRIPTION_EXPIRED_PATH = "/subscription-expired";

  const fetchSubscriptionDetails = useCallback(async () => {
    if (!sessionDetails?.orgId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate({
        method: "GET",
        url: getUrl(SUBSCRIPTION_DETAILS_PATH),
      });

      const subscriptionDetails = response?.data?.subscription;
      if (!subscriptionDetails) {
        setAlertDetails({
          type: "error",
          content: "Failed to fetch subscription details",
        });

        handleSubExpiredNavigation();
        return;
      }

      updateSubscriptionPlan({
        subscriptionId: subscriptionDetails?.subscription_id,
        planType: subscriptionDetails?.plan_type,
        product: subscriptionDetails?.product,
        isSubscribed: subscriptionDetails?.is_subscribed,
        isCancelled: subscriptionDetails?.is_cancelled,
        remainingDays: subscriptionDetails?.remaining_days,
        nextBillingDate: subscriptionDetails?.next_billing_date,
      });
    } catch (error) {
      setAlertDetails(handleException(error));
      handleSubExpiredNavigation();
    } finally {
      setIsLoading(false);
    }
  }, [updateSubscriptionPlan]);

  const handleSubExpiredNavigation = () => {
    if (location.pathname === `/${sessionDetails?.orgId}/pricing`) {
      return;
    }

    navigate(SUBSCRIPTION_EXPIRED_PATH);
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  if (isLoading) {
    return <GenericLoader />;
  }

  return <Outlet />;
}

export { UnstractSubscriptionCheck };
